// Kontaktsuche der Schadenmappe — gemeinsam für den Kontakt des Schadens
// („Schadenart“) und die Kontakte beim Versicherer („Abschluss“): Suche mit
// Entprellung, Trefferliste, gewählter Kontakt mit „Kontakt ändern“, „Abbrechen“
// zurück zum vorigen. Einer Vorauswahl ohne Namen wird der Name nachgeladen.
import * as React from "react";
import { apiUrl } from "../../../utils/api";
import { contactRefFromRow, fetchJson, linkQueryUrls } from "../../vorgang/detail/linkFetch";
import { kontaktAdresse, kontaktLabel } from "../../vorgang/detail/linkSlots";

export interface Verweis {
  id: number;
  label: string;
}

/** Entprellung der Kontaktsuche (wie im „Neuer Schaden“-Fenster). */
const SUCHE_MS = 280;

interface Treffer {
  id: number;
  label: string;
  adresse: string | null;
}

/** Anzeigename einer Kontakt-Zeile: `displayName`, sonst „Nachname, Vorname“. */
export function kontaktName(row: any): string {
  const anzeige = typeof row?.displayName === "string" ? row.displayName.trim() : "";
  return anzeige || kontaktLabel(contactRefFromRow(row));
}

/**
 * Vorauswahl ohne Namen (z. B. `/schaeden/neu?contactId=…`): den Namen einmal
 * nachladen und über `onName` melden (gleiche Id).
 */
export function useKontaktName(kontakt: Verweis | null, onName: (k: Verweis) => void): void {
  const onNameRef = React.useRef(onName);
  React.useEffect(() => {
    onNameRef.current = onName;
  });
  const kontaktId = kontakt?.id ?? null;
  const ohneName = kontakt !== null && kontakt.label === "";
  React.useEffect(() => {
    if (!ohneName || kontaktId === null) return;
    let abgebrochen = false;
    fetch(apiUrl(`contact/${kontaktId}`))
      .then((res) => (res.ok ? res.json() : null))
      .then((row) => {
        if (!abgebrochen && row) onNameRef.current({ id: kontaktId, label: kontaktName(row) });
      })
      .catch(() => undefined);
    return () => {
      abgebrochen = true;
    };
  }, [ohneName, kontaktId]);
}

export interface KontaktSucheProps {
  kontakt: Verweis | null;
  /** Der zuletzt gewählte Kontakt — nach „Kontakt ändern“ das Ziel von „Abbrechen“. */
  vorheriger: Verweis | null;
  /** Wahl eines Treffers, „Abbrechen“ (→ `vorheriger`) und „Kontakt ändern“ (→ `null`). */
  onWahl: (k: Verweis | null) => void;
  /** Nachgeladener Name einer Vorauswahl ohne Namen (gleiche Id); ohne Angabe `onWahl`. */
  onName?: (k: Verweis) => void;
  /** Zugänglicher Name des Suchfelds. */
  suchLabel?: string;
  platzhalter?: string;
  /** Wenn gesetzt, steht neben dem gewählten Kontakt ein „Entfernen“-Knopf. */
  onEntfernen?: () => void;
}

export function KontaktSuche({
  kontakt,
  vorheriger,
  onWahl,
  onName,
  suchLabel = "Kontakt suchen",
  platzhalter = "Kontakt suchen (Name, Firma) …",
  onEntfernen,
}: KontaktSucheProps) {
  const [suche, setSuche] = React.useState("");
  const [treffer, setTreffer] = React.useState<Treffer[]>([]);
  const [laedt, setLaedt] = React.useState(false);

  // Vorauswahl ohne Namen (z. B. `/schaeden/neu?contactId=…`): Namen einmal nachladen.
  useKontaktName(kontakt, onName ?? onWahl);

  React.useEffect(() => {
    const begriff = suche.trim();
    if (begriff.length < 2) {
      setTreffer([]);
      setLaedt(false);
      return;
    }
    let abgebrochen = false;
    setLaedt(true);
    const t = setTimeout(() => {
      void fetchJson(linkQueryUrls.contactsSearch(begriff)).then((rows) => {
        if (abgebrochen) return;
        setTreffer(
          rows
            .map((row) => ({
              id: Number(row.id),
              label: kontaktName(row),
              adresse: kontaktAdresse(contactRefFromRow(row)),
            }))
            .filter((x) => x.id > 0),
        );
        setLaedt(false);
      });
    }, SUCHE_MS);
    return () => {
      abgebrochen = true;
      clearTimeout(t);
    };
  }, [suche]);

  if (kontakt) {
    return (
      <div className="smp-gewaehlt">
        <span className="smp-gewaehlt-name">{kontakt.label || `Kontakt #${kontakt.id}`}</span>
        <button
          type="button"
          className="tbtn"
          onClick={() => {
            setSuche("");
            onWahl(null);
          }}
        >
          Kontakt ändern
        </button>
        {onEntfernen && (
          <button type="button" className="tbtn" onClick={onEntfernen}>
            Entfernen
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="smp-suche">
      <div className="smp-suche-zeile">
        <input
          type="search"
          className="smp-suche-feld"
          aria-label={suchLabel}
          placeholder={platzhalter}
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        {vorheriger && (
          <button
            type="button"
            className="tbtn"
            onClick={() => {
              setSuche("");
              onWahl(vorheriger);
            }}
          >
            Abbrechen
          </button>
        )}
      </div>
      {laedt && <div className="smp-hinweis">Suche …</div>}
      {!laedt && suche.trim().length >= 2 && treffer.length === 0 && (
        <div className="smp-hinweis">Keine Treffer.</div>
      )}
      {treffer.length > 0 && (
        <ul className="smp-treffer">
          {treffer.map((t) => (
            <li key={t.id}>
              <button type="button" className="smp-treffer-zeile" onClick={() => onWahl({ id: t.id, label: t.label })}>
                <span className="smp-treffer-name">{t.label}</span>
                {t.adresse && <span className="smp-treffer-adresse">{t.adresse}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
